import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format, differenceInMinutes, parseISO, addMinutes } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SearchDropdown from "../applicationUi/components/SearchDropdown";
import {
  createResourceSchedule,
  updateResourceSchedule,
} from "../store/thunks/resourceScheduleThunk";
import { toast } from 'react-toastify';

const BookResourceModal = ({
  onClose,
  resource,
  selectedTime,
  currentDate,
  existingSchedule,
}) => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedResource, setSelectedResource] = useState(resource?._id || "");
  const [selectedClinicians, setSelectedClinicians] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);

  const clinics = useSelector((state) => state.clinics?.clinics || []);
  const defaultClinic = useSelector((state) => state.clinics?.selectedClinic);
  const resources = useSelector((state) => state.resources?.resources || []);
  const loading = useSelector((state) => state.resourceSchedules?.loading);

  // Prepopulate form if updating an existing schedule
  useEffect(() => {
    if (existingSchedule) {
      setTitle(existingSchedule.title || "");
      setDescription(existingSchedule.description || "");
      setStartDate(parseISO(existingSchedule.start));
      setEndDate(parseISO(existingSchedule.end));
      setSelectedResource(
        existingSchedule.resource?._id || existingSchedule.resource
      );
      setSelectedClinicians(existingSchedule.clinicians || []);
    } else if (selectedTime && currentDate) {
      const [hours, minutes] = selectedTime.split(":").map(Number);
      const startDateTime = new Date(currentDate);
      startDateTime.setHours(hours, minutes, 0, 0);
      setStartDate(startDateTime);
      setEndDate(addMinutes(startDateTime, 60));
    }
  }, [existingSchedule, selectedTime, currentDate]);

  useEffect(() => {
    if (resource && resource._id) {
      setSelectedResource(resource._id);
    }
  }, [resource]);

  // Set default clinic
  useEffect(() => {
    if (defaultClinic && !selectedClinic) {
      setSelectedClinic(defaultClinic._id);
    }
  }, [defaultClinic, selectedClinic]);

  const calculateDuration = () => {
    if (startDate && endDate) {
      return differenceInMinutes(endDate, startDate);
    }
    return null;
  };

  // Update endDate when startDate changes (only in create mode)
  useEffect(() => {
    if (!existingSchedule && startDate) {
      setEndDate(addMinutes(startDate, 60));
    }
  }, [startDate, existingSchedule]);

  const handleAddClinician = (clinician) => {
    if (!selectedClinicians.find((c) => c._id === clinician._id)) {
      setSelectedClinicians([...selectedClinicians, clinician]);
    }
  };

  const handleRemoveClinician = (clinicianId) => {
    setSelectedClinicians(
      selectedClinicians.filter((c) => c._id !== clinicianId)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate || !selectedResource || !selectedClinic) {
      toast.warning("Please fill all required fields");
      return;
    }
    const duration = calculateDuration();
    if (duration <= 0) {
      toast.warning("End time must be after start time.");
      return;
    }
    const scheduleData = {
      resource: selectedResource,
      clinicians: selectedClinicians.map((c) => c._id),
      title: title || "Resource Booking",
      description,
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      status: "Booked",
      meeting: existingSchedule?.meeting || null,
    };
    try {
      if (existingSchedule) {
        await dispatch(
          updateResourceSchedule({
            clinicId: selectedClinic,
            scheduleId: existingSchedule._id,
            scheduleData,
          })
        );
      } else {
        await dispatch(
          createResourceSchedule({
            clinicId: selectedClinic,
            scheduleData,
          })
        );
      }
      onClose();
    } catch (error) {
      console.error("Failed to book resource:", error);
      toast.error("Failed to book resource");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-sm shadow-lg w-full max-w-lg p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center border-b pb-3">
          <h2 className="text-lg font-semibold text-gray-900">
            {existingSchedule ? "Update Booking" : "Book Resource"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-sm hover:bg-gray-100 transition">
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Clinic Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clinic
            </label>
            <select
              value={selectedClinic || ""}
              onChange={(e) => setSelectedClinic(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500"
              required>
              <option value="">Select a clinic</option>
              {clinics.map((clinic) => (
                <option key={clinic._id} value={clinic._id}>
                  {clinic.name}
                </option>
              ))}
            </select>
          </div>

          {/* Resource Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resource
            </label>
            <select
              value={selectedResource}
              onChange={(e) => setSelectedResource(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500"
              required>
              <option value="">Select a resource</option>
              {resources.map((res) => (
                <option key={res._id} value={res._id}>
                  {res.name}
                </option>
              ))}
            </select>
          </div>

          {/* Clinicians Selection */}
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">
              Clinicians
            </p>
            <div className="space-y-2">
              <SearchDropdown
                prefix=":user:"
                displayFields={["firstName", "lastName", "email"]}
                onSelection={handleAddClinician}
                placeholder="Search for clinicians"
              />
              {selectedClinicians.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedClinicians.map((clinician) => (
                    <div
                      key={clinician._id}
                      className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-sm text-sm">
                      <span>
                        {clinician.firstName} {clinician.lastName}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveClinician(clinician._id)}
                        className="ml-2 text-blue-600 hover:text-blue-800">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Booking title"
              className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details about this booking"
              className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMMM d, yyyy h:mm aa"
                className="w-[220px] px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMMM d, yyyy h:mm aa"
                className="w-[220px] px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 transition">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}>
              {loading
                ? existingSchedule
                  ? "Updating..."
                  : "Booking..."
                : existingSchedule
                ? "Update Booking"
                : "Book Resource"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookResourceModal;
